import { CrossIcon } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import { useState } from 'react';
import {
  TASK_LABEL_MAX_LENGTH,
  cleanTaskLabel,
  countTemplateTasks,
  getQualificationTaskRows,
  isTaskLabelListed,
} from './templateFormData';
import { templateText } from './templateTexts';

/** The tasks of a job description: the qualification's, ticked or not, and
 *  the ones the admin adds on top. */
const TemplateTasksSection = ({ form, setForm }) => {
  const [newLabel, setNewLabel] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const rows = getQualificationTaskRows(form);
  const companyTasks = form.companyTasks || [];
  const { selected, total } = countTemplateTasks(form);
  const cleanLabel = cleanTaskLabel(newLabel);

  const addTask = () => {
    if (!cleanLabel) return;
    if (isTaskLabelListed(cleanLabel, form)) {
      setIsDuplicate(true);
      return;
    }
    setForm((prev) => ({
      ...prev,
      companyTasks: [cleanLabel, ...(prev.companyTasks || [])],
    }));
    setNewLabel('');
    setIsDuplicate(false);
  };

  const removeTask = (label) =>
    setForm((prev) => ({
      ...prev,
      companyTasks: (prev.companyTasks || []).filter((task) => task !== label),
    }));

  const toggleTask = (row) =>
    setForm((prev) => ({
      ...prev,
      taskChecks: { ...(prev.taskChecks || {}), [row.key]: !row.checked },
    }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <span className="ft-xl font-medium text-gray-900">
          {templateText('tasks.title')}
        </span>
        <span className="ft-sm text-gray-500">
          {templateText('tasks.description')}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-end gap-2">
          <div className="flex-1 min-w-0">
            <StyledInput
              type="text"
              label={
                <span className="sr-only">
                  {templateText('tasks.addPlaceholder')}
                </span>
              }
              value={newLabel}
              maxLength={TASK_LABEL_MAX_LENGTH}
              onChange={(event) => {
                setNewLabel(event.target.value);
                setIsDuplicate(false);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addTask();
              }}
              placeholder={templateText('tasks.addPlaceholder')}
            />
          </div>
          {/* Centred on the 40 px input, below its screen-reader label row. */}
          <div className="h-10 flex items-center">
            <Button
              size="SM"
              color={cleanLabel ? 'SECONDARY' : 'DISABLED'}
              onClick={addTask}
              disabled={!cleanLabel}
            >
              {templateText('tasks.addButton')}
            </Button>
          </div>
        </div>
        {isDuplicate ? (
          <span className="ft-sm text-red-500">
            {templateText('tasks.duplicate')}
          </span>
        ) : null}
      </div>

      <span className="text-sm text-gray-700">
        <strong>
          {templateText(
            selected > 1
              ? 'tasks.selectedCountOther'
              : 'tasks.selectedCountOne',
            { count: selected }
          )}
        </strong>{' '}
        {templateText(
          selected > 1 ? 'tasks.selectedOfOther' : 'tasks.selectedOfOne',
          { total }
        )}
      </span>

      {rows.length === 0 && companyTasks.length === 0 ? (
        <span className="text-sm text-gray-500">
          {templateText('tasks.noProposal')}
        </span>
      ) : (
        <ul className="flex flex-col gap-2">
          {companyTasks.map((label) => (
            <li
              key={`company:${label}`}
              className="flex items-center justify-between gap-3 px-3 py-2 border-2 border-yellow-500"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-gray-900 break-words">{label}</span>
                <span className="text-sm text-gray-400">
                  {templateText('tasks.addedByCompany')}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeTask(label)}
                className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg hover:bg-gray-100"
              >
                <span className="sr-only">
                  {templateText('tasks.remove', { label })}
                </span>
                <span aria-hidden="true">
                  <CrossIcon wh={20} color="#374151" />
                </span>
              </button>
            </li>
          ))}
          {rows.map((row) => (
            <li key={row.key}>
              <label
                className={`flex items-center gap-3 px-3 py-2 border-2 cursor-pointer ${
                  row.checked ? 'border-yellow-500' : 'border-gray-100'
                }`}
              >
                <input
                  type="checkbox" className="checkbox-primary"
                  checked={row.checked}
                  onChange={() => toggleTask(row)}
                />
                <span className="text-sm text-gray-900 break-words">
                  {row.label}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TemplateTasksSection;
